/**
 * Universe map — the sector-grouped dot field over the merged universe rows
 * (positions ∪ pool ∪ watchlist), echoing the landing page's "living map"
 * visual on real data. Pure presentational: the tab hands it the same
 * `UniverseRow[]` that feeds SymbolTable.
 *
 * Unlike the landing page's seeded-random scatter (decorative mock data), the
 * layout here is MEANINGFUL and deterministic:
 *   - x: one vertical band per sector (alphabetical; pool-less symbols land in
 *     a trailing "unsorted" band), with a per-symbol hash jitter inside the band
 *     so the field reads organic rather than gridded.
 *   - y: symbols within a sector are spread evenly (sorted order), so dots
 *     never stack no matter how many names share a sector.
 *   - kind precedence: held (in position, --ok green) > watch (on the CSP
 *     watchlist, --accent ember) > candidate (pool-only, muted).
 *   - earnings blackout adds a danger ring + a tooltip note.
 *
 * Held and watch dots carry visible symbol labels; candidates reveal theirs on
 * hover (title). Re-renders are cheap and stable: same rows → same layout.
 */

import type { UniverseRow } from '../tabs/UniverseTab';

export type DotKind = 'held' | 'watch' | 'candidate';

export interface UniverseDot {
  symbol: string;
  sector: string;
  kind: DotKind;
  /** Percent coordinates inside the map box (0–100). */
  x: number;
  y: number;
  earningsBlackout: boolean;
  /** Hover text: symbol + membership + earnings note. */
  title: string;
}

export interface UniverseLayout {
  /** Sector bands, in render order, with their center x (percent). */
  sectors: Array<{ name: string; centerX: number }>;
  dots: UniverseDot[];
}

/** Label for rows whose symbol isn't in the pool (no sector known). */
const UNSORTED = 'unsorted';

/** Deterministic [0,1) hash of a symbol (FNV-1a folded). Stable across renders
 *  and sessions so the field never jumps between loads. */
export function symbolHash(symbol: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < symbol.length; i++) {
    h ^= symbol.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0x100000000;
}

function kindOf(row: UniverseRow): DotKind {
  if (row.inPosition) return 'held';
  if (row.inWatchlist) return 'watch';
  return 'candidate';
}

function titleOf(row: UniverseRow, kind: DotKind): string {
  const membership =
    kind === 'held' ? 'held' : kind === 'watch' ? 'watchlist' : 'pool candidate';
  const earnings = row.earningsBlackout
    ? ` · earnings blackout (${row.nextEarnings ?? '?'})`
    : row.nextEarnings
      ? ` · earnings ${row.nextEarnings}`
      : '';
  return `${row.symbol} — ${membership}${earnings}`;
}

/** Pure layout: rows → sector bands + positioned dots. Exported for tests. */
export function layoutUniverse(rows: UniverseRow[]): UniverseLayout {
  // Sector bands: alphabetical, "unsorted" (no pool sector) always last.
  const sectorNames = [...new Set(rows.map((r) => r.sector ?? UNSORTED))].sort(
    (a, b) =>
      a === UNSORTED ? 1 : b === UNSORTED ? -1 : a.localeCompare(b),
  );
  const bandWidth = 100 / Math.max(sectorNames.length, 1);
  const bandStart = new Map(sectorNames.map((s, i) => [s, i * bandWidth]));

  const bySector = new Map<string, UniverseRow[]>();
  for (const row of rows) {
    const sector = row.sector ?? UNSORTED;
    const list = bySector.get(sector) ?? [];
    list.push(row);
    bySector.set(sector, list);
  }

  const dots: UniverseDot[] = [];
  for (const [sector, list] of bySector) {
    // Sorted within the band so the even vertical spread is stable.
    list.sort((a, b) => a.symbol.localeCompare(b.symbol));
    const start = bandStart.get(sector)!;
    list.forEach((row, i) => {
      const kind = kindOf(row);
      dots.push({
        symbol: row.symbol,
        sector,
        kind,
        // 20–80% of the band horizontally (hash jitter), 10–90% vertically
        // (even spread by rank — no two dots in a band share a y).
        x: start + bandWidth * (0.2 + 0.6 * symbolHash(row.symbol)),
        y: 10 + 80 * ((i + 0.5) / list.length),
        earningsBlackout: row.earningsBlackout,
        title: titleOf(row, kind),
      });
    });
  }

  return {
    sectors: sectorNames.map((name) => ({
      name,
      centerX: bandStart.get(name)! + bandWidth / 2,
    })),
    dots,
  };
}

export default function UniverseMap({ rows }: { rows: UniverseRow[] }) {
  if (rows.length === 0) return null;
  const { sectors, dots } = layoutUniverse(rows);

  return (
    <figure className="universe-map" aria-label="Universe map">
      <div className="universe-map-field">
        {dots.map((d) => {
          // Near the right edge the trailing label would clip against the
          // field boundary (overflow: hidden) — flip it to the left side.
          const labelBefore = d.x > 85;
          const label = d.kind !== 'candidate' && (
            <span className={`universe-dot-label universe-dot-label--${d.kind}`}>{d.symbol}</span>
          );
          return (
            <span key={d.symbol} className="universe-map-item" style={{ left: `${d.x}%`, top: `${d.y}%` }}>
              {labelBefore && label}
              <span
                className={
                  `universe-dot universe-dot--${d.kind}` +
                  (d.earningsBlackout ? ' universe-dot--blackout' : '')
                }
                role="img"
                aria-label={d.title}
                title={d.title}
                data-symbol={d.symbol}
              />
              {!labelBefore && label}
            </span>
          );
        })}
        {sectors.map((s) => (
          <span key={s.name} className="universe-map-sector" style={{ left: `${s.centerX}%` }}>
            {s.name.replace(/_/g, ' ')}
          </span>
        ))}
      </div>
      <figcaption className="universe-map-legend">
        <span className="universe-legend-item">
          <span className="universe-dot universe-dot--held" /> held
        </span>
        <span className="universe-legend-item">
          <span className="universe-dot universe-dot--watch" /> watchlist
        </span>
        <span className="universe-legend-item">
          <span className="universe-dot universe-dot--candidate" /> candidate
        </span>
        <span className="universe-legend-item">
          <span className="universe-dot universe-dot--candidate universe-dot--blackout" /> earnings blackout
        </span>
      </figcaption>
    </figure>
  );
}
